import { useState, type ReactNode } from 'react';
import type { ContentBlock } from '@fametrue/shared';
import { ContentBlocks } from './ContentBlocks';

/**
 * Blocco a fisarmonica: le card del Level 2 e le sezioni del Level 3.
 *
 * `num` arriva sempre **dall'esterno** e non è l'indice della posizione nel
 * `map` che rende l'elenco. Dal 16/10 è un progressivo sulle card presenti,
 * calcolato una volta sola da `decisionSections()`: prima era un letterale che
 * saltava quando un blocco condizionale mancava. In entrambi i casi la regola
 * per chi tocca questo file è la stessa — il numero non si deduce qui.
 *
 * Stato `open` locale con le classi del riferimento. Divergenza dichiarata: il
 * click che apre/chiude sta sull'header, non sul contenitore (altrimenti ogni
 * click nel corpo richiuderebbe il blocco).
 */
interface AccordionProps {
  num: string;
  title: string;
  /** Può contenere elementi: la classifica ci monta il selettore di ordinamento. */
  subtitle?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}

export function Accordion({
  num,
  title,
  subtitle,
  defaultOpen = false,
  children,
}: AccordionProps): ReactNode {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={open ? 'accordion open' : 'accordion'}>
      <div
        className="accordion-header"
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <span className="accordion-num">{num}</span>
        <span className="accordion-title">{title}</span>
        {subtitle ? <span className="accordion-subtitle">{subtitle}</span> : null}
        <svg
          className="accordion-chevron"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
      <div className="accordion-body">
        <div className="accordion-content">{children}</div>
      </div>
    </div>
  );
}

/** Etichetta di sezione dentro un blocco. */
export function AcLabel({ children }: { children: ReactNode }): ReactNode {
  return <span className="ac-label">{children}</span>;
}

/** Elenco puntato dei blocchi, con il marcatore del design system. */
export function AcList({ items }: { items: ReactNode[] }): ReactNode {
  return (
    <ul className="ac-list">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

/**
 * Testo di un blocco Diagnosi: la prosa di `content` (HTML inline) e poi i
 * `blocks` strutturati. `content` è interpolato in `<p>…</p>` dentro un `div`,
 * come fa il riferimento: un `<div>` nel contenuto chiude il `<p>` implicito e
 * la resa coincide; con l'HTML messo direttamente sul `<p>` non coinciderebbe.
 */
export function AcContent({
  html,
  blocks,
}: {
  html?: string | undefined;
  blocks?: ContentBlock[] | undefined;
}): ReactNode {
  return (
    <>
      {html !== undefined ? (
        <div dangerouslySetInnerHTML={{ __html: `<p>${wrapTables(html)}</p>` }} />
      ) : null}
      {blocks && blocks.length > 0 ? <ContentBlocks blocks={blocks} /> : null}
    </>
  );
}

/**
 * [YBD] Ogni `<table>` del markup finisce dentro `<div class="table-scroll">`:
 * sotto i 900 px è quel div a scorrere in orizzontale, con la prima colonna
 * agganciata (app.css), invece dell'intero `.accordion-content` come fa il
 * riferimento. Il `<div>` chiude il `<p>` implicito esattamente come lo
 * chiudeva il `<table>`, quindi la struttura del DOM attorno non cambia.
 * Le tabelle non sono annidate nei contenuti del committente.
 */
export function wrapTables(html: string): string {
  return html.replace(
    /<table\b[\s\S]*?<\/table>/g,
    (table) => `<div class="table-scroll">${table}</div>`,
  );
}
